import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Quote, Send } from "lucide-react";
import { EASE, Kicker, Reveal, SectionHead } from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { fetchTestimonials, submitTestimonial } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";

export default function Testimonials() {
  usePageMeta(
    "Testimonials | Diamond's Divine Arts",
    "Read kind words from collectors and share your own experience with Diamond's Divine Arts — every testimonial is reviewed by Natalia before it appears."
  );

  const [approved, setApproved] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", quote: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetchTestimonials()
      .then(setApproved)
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name.");
    if (form.quote.trim().length < 10)
      return toast.error("Please write a few words about your experience.");
    setSending(true);
    try {
      await submitTestimonial({
        name: form.name.trim(),
        location: form.location.trim(),
        quote: form.quote.trim(),
      });
      setSent(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="divine-rays relative overflow-hidden pb-16 pt-16 md:pb-20 md:pt-24" data-testid="testimonials-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>Testimonials</Kicker>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#1C1815] sm:text-5xl lg:text-6xl">
              Words that <span className="italic text-[#9A7A3F]">encourage</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#4A423A]">
              Kind words from collectors, families, and ministries. Every testimonial is personally
              reviewed by Natalia before it appears here.
            </p>
          </Reveal>
        </div>
      </section>

      <section className={approved.length === 0 ? "py-14 md:py-16" : "py-20 md:py-28"} data-testid="testimonials-list-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {approved.length === 0 ? (
            <Reveal>
              <div className="mx-auto max-w-xl rounded-sm border border-[#D4AF37]/30 bg-[#F4EFE6]/60 px-8 py-10 text-center" data-testid="testimonials-empty-note">
                <p className="font-serif text-xl font-light leading-relaxed text-[#2A241F]">
                  Be the first to share your experience.
                </p>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#4A423A]">
                  Approved testimonials from collectors and families will appear here.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid gap-8 md:grid-cols-3">
              {approved.map((t, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <figure
                    className="flex h-full flex-col rounded-sm border border-[#D4AF37]/30 bg-[#F4EFE6]/70 p-8"
                    data-testid={`testimonial-card-${i}`}
                  >
                    <Quote className="h-6 w-6 text-[#D4AF37]" />
                    <blockquote className="mt-5 flex-1 font-serif text-lg font-light leading-relaxed text-[#2A241F]">
                      “{t.quote}”
                    </blockquote>
                    <figcaption className="mt-6 border-t border-[#D4AF37]/30 pt-4">
                      <span className="block text-sm font-medium text-[#1C1815]">{t.name || t.author}</span>
                      <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C7E70]">
                        {t.location || t.role}
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-y border-[#D4AF37]/20 bg-[#171310] py-20 md:py-28" data-testid="testimonial-form-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <SectionHead
              dark
              kicker="Share Your Experience"
              title="Your words could encourage someone else"
              copy="Collected a piece, booked a consultation, or commissioned artwork? Tell others what it meant to you. Your testimonial is emailed straight to Natalia, and it appears on this page once she approves it."
            />
          </div>
          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="rounded-sm border border-[#D4AF37]/40 bg-[#1F1A15] p-10 text-center md:p-14"
                data-testid="testimonial-success"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#171310]">
                  <Check className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h2 className="mt-7 font-serif text-3xl font-light text-[#FAF7F2]">
                  Thank you for sharing.
                </h2>
                <p className="mx-auto mt-4 max-w-md text-sm font-light leading-relaxed text-[#B7AA99]">
                  Your testimonial has been sent to Natalia for approval — it will appear on this
                  page once she reviews it.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", location: "", quote: "" });
                  }}
                  className="btn-ghost-light mt-8"
                  data-testid="testimonial-send-another"
                >
                  Write Another
                </button>
              </motion.div>
            ) : (
              <Reveal delay={0.1}>
                <form
                  onSubmit={submit}
                  className="rounded-sm border border-[#D4AF37]/30 bg-[#1F1A15] p-6 sm:p-10"
                  data-testid="testimonial-form"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="kicker !text-[#B89759]">Your Name</span>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="e.g. Sarah M."
                        className="field mt-2.5 !border-[#D4AF37]/30 !bg-[#171310] !text-[#FAF7F2] placeholder:!text-[#8C7E70] focus:!border-[#D4AF37]"
                        data-testid="testimonial-name-input"
                      />
                    </label>
                    <label className="block">
                      <span className="kicker !text-[#B89759]">Location (optional)</span>
                      <input
                        type="text"
                        value={form.location}
                        onChange={(e) => set("location", e.target.value)}
                        placeholder="e.g. Greenwich, CT"
                        className="field mt-2.5 !border-[#D4AF37]/30 !bg-[#171310] !text-[#FAF7F2] placeholder:!text-[#8C7E70] focus:!border-[#D4AF37]"
                        data-testid="testimonial-location-input"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="kicker !text-[#B89759]">Your Testimonial</span>
                      <textarea
                        rows={5}
                        value={form.quote}
                        onChange={(e) => set("quote", e.target.value)}
                        placeholder="What did your piece, consultation, or experience with Diamond's Divine Arts mean to you?"
                        className="field mt-2.5 resize-none !border-[#D4AF37]/30 !bg-[#171310] !text-[#FAF7F2] placeholder:!text-[#8C7E70] focus:!border-[#D4AF37]"
                        data-testid="testimonial-quote-input"
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn-gold mt-8 w-full disabled:opacity-60 sm:w-auto"
                    data-testid="testimonial-submit-button"
                  >
                    {sending ? "Sending…" : "Send for Approval"} <Send className="h-4 w-4" />
                  </button>
                  <p className="mt-5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-[#8C7E70]">
                    Sent privately to Natalia — nothing is published without her approval.
                  </p>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to Begin Your Own Story?"
        copy="Explore the collection or commission a piece rooted in your faith and your space."
      />
    </>
  );
}
